/**
 * Individual action executors for chat actions
 */

import React from 'react';
import { ChatAction, ChatMessage as ChatMessageType } from '../../types/chat';
import { apiClient } from '../../lib/api';

export interface ActionExecutorContext {
  tableId?: string;
  setMessages: React.Dispatch<React.SetStateAction<ChatMessageType[]>>;
  onActionTriggered?: (action: { type: string; payload: any }) => void;
  getSessionId?: () => string | null;
}

export const executePreviewDataset = async (
  action: ChatAction,
  context: ActionExecutorContext
): Promise<void> => {
  try {
    const previewData = await apiClient.previewDataset(
      action.parameters?.searchQuery,
      action.parameters?.tableType,
      action.parameters?.customTypeDescription
    );
    
    const previewMessage: ChatMessageType = {
      id: (Date.now() + 1).toString(),
      role: 'assistant',
      content: 'Here\'s your dataset preview:',
      timestamp: new Date(),
      actions: [{
        type: 'generate_dataset',
        parameters: {
          ...action.parameters,
          previewData,
          rowCount: action.parameters?.rowCount || 100
        },
        description: 'Generate Dataset'
      }]
    };
    context.setMessages(prev => [...prev, previewMessage]);
  } catch (error) {
    console.error('Error loading preview:', error);
    const errorMessage: ChatMessageType = {
      id: (Date.now() + 1).toString(),
      role: 'assistant',
      content: `❌ Failed to load dataset preview: ${error instanceof Error ? error.message : 'Unknown error'}`,
      timestamp: new Date()
    };
    context.setMessages(prev => [...prev, errorMessage]);
  }
};

export const executeGenerateDataset = (
  action: ChatAction,
  context: ActionExecutorContext
): void => {
  if (context.onActionTriggered) {
    // Get current session ID
    const sessionId = context.getSessionId ? context.getSessionId() : null;
    
    context.onActionTriggered({
      type: 'generate_dataset',
      payload: {
        actionParameters: action.parameters,
        sessionId: sessionId
      }
    });
  }
};

export const executeFindMoreResults = (
  action: ChatAction,
  context: ActionExecutorContext
): void => {
  if (context.tableId && context.onActionTriggered) {
    context.onActionTriggered({
      type: 'find_more_results',
      payload: {
        tableId: context.tableId,
        actionParameters: action.parameters
      }
    });
  }
};

export const executeEnrichColumn = (
  action: ChatAction,
  context: ActionExecutorContext
): void => {
  if (context.tableId && context.onActionTriggered) {
    context.onActionTriggered({
      type: 'enrich_column',
      payload: {
        tableId: context.tableId,
        actionParameters: action.parameters
      }
    });
  }
};

export const executeSendEmails = (
  action: ChatAction,
  context: ActionExecutorContext
): void => {
  if (context.tableId && context.onActionTriggered) {
    context.onActionTriggered({
      type: 'show_email_modal',
      payload: {
        tableId: context.tableId,
        actionParameters: action.parameters,
        suggestedEmailData: {
          subject: action.parameters?.email_subject || '',
          body: action.parameters?.email_body || '',
          suggestedEmailColumn: action.parameters?.email_column || ''
        }
      }
    });
  }
};